import React, { useEffect, useState } from 'react';
import { Loader2, Clapperboard, Check, RefreshCw } from 'lucide-react';
import { getCrewCatalog, resolveCrew, type CrewRole, type CrewRoleDef, type CrewPreset } from '../../services/studioService';
import { TYPE } from '../../constants/typeScale';

interface Props {
  /** Called with the resolved catalog agent ids for the chosen crew — the parent maps them to chat members. */
  onPick: (agentIds: string[]) => void;
}

/**
 * MediaCrewPicker — "staff your studio". Start from a named PRESET crew (one tap) or pick a FLAVOR per production
 * ROLE (director required; composer/writer/legal optional). Reuses existing catalog agents behind plain-English
 * roles. Craft stays hidden: you pick a director's STYLE, never a model. Resolves server-side (authoritative
 * allowlist) → the parent adds the crew to the new chat. Monochrome, Lucide, four states.
 */
const MediaCrewPicker: React.FC<Props> = ({ onPick }) => {
  const [roles, setRoles] = useState<CrewRoleDef[]>([]);
  const [presets, setPresets] = useState<CrewPreset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [picks, setPicks] = useState<Partial<Record<CrewRole, string>>>({});
  const [preset, setPreset] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);

  const load = () => {
    setLoading(true); setError('');
    getCrewCatalog()
      .then((c) => {
        setRoles(c.roles); setPresets(c.presets);
        // Default: the first (versatile) director selected, nothing else — a first-timer can just hit Add.
        const dir = c.roles.find((r) => r.role === 'director')?.flavors[0]?.agentId;
        if (dir) setPicks({ director: dir });
      })
      .catch(() => setError('Could not load the crew.'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // Choosing a preset fills every role from it (and clears manual picks); tapping a role flavor switches to manual.
  const choosePreset = (p: CrewPreset) => { setPreset(p.key); setPicks(p.crew); };
  const chooseFlavor = (role: CrewRole, agentId: string) => {
    setPreset(null);
    setPicks((prev) => ({ ...prev, [role]: prev[role] === agentId && !isRequired(role) ? undefined : agentId }));
  };
  const isRequired = (role: CrewRole) => roles.find((r) => r.role === role)?.required;

  const add = async () => {
    setResolving(true); setError('');
    try {
      const ids = await resolveCrew({ preset: preset || undefined, picks });
      if (!ids.length) { setError('Pick at least a director.'); return; }
      onPick(ids);
    } catch { setError('Could not set up the crew. Please try again.'); }
    finally { setResolving(false); }
  };

  if (loading) return <div className="flex justify-center p-8"><Loader2 className="h-5 w-5 animate-spin text-secondary-400" /></div>;

  // Load failed with no data → a clear error + Retry, never a blank picker with a dead Add button.
  if (error && !roles.length) return (
    <div className="flex flex-col items-center gap-3 p-8 text-center">
      <p className={`${TYPE.body} text-secondary-500`}>{error}</p>
      <button onClick={load} className={`inline-flex items-center gap-1.5 rounded-lg border border-secondary-300 dark:border-secondary-600 px-3 py-1.5 ${TYPE.control} font-medium text-secondary-700 dark:text-secondary-200 hover:bg-secondary-50 dark:hover:bg-secondary-800`}><RefreshCw className="h-3.5 w-3.5" /> Try again</button>
    </div>
  );

  const crewCount = new Set(Object.values(picks).filter(Boolean)).size;

  return (
    <div className="space-y-5">
      {/* Preset crews — one tap to staff the common jobs */}
      {presets.length > 0 && (
        <div>
          <p className={`mb-2 ${TYPE.label} text-secondary-400`}>Start from a crew</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {presets.map((p) => (
              <button key={p.key} onClick={() => choosePreset(p)} title={p.hint}
                className={`rounded-xl border p-3 text-left transition ${preset === p.key
                  ? 'border-secondary-900 dark:border-white bg-secondary-50 dark:bg-secondary-800'
                  : 'border-secondary-200 dark:border-secondary-600 hover:border-secondary-400'}`}>
                <span className={`flex items-center gap-1.5 ${TYPE.sectionTitle} text-secondary-900 dark:text-white`}><Clapperboard className="h-3.5 w-3.5" />{p.label}</span>
                <span className={`mt-0.5 block ${TYPE.meta} text-secondary-500 dark:text-secondary-400 line-clamp-2`}>{p.hint}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Per-role flavor pickers */}
      {roles.map((r) => (
        <div key={r.role}>
          <p className={`mb-2 ${TYPE.label} text-secondary-400`}>
            {r.label}{r.required ? ' · required' : ' · optional'}
          </p>
          <div className="flex flex-wrap gap-2">
            {r.flavors.map((f) => {
              const on = picks[r.role] === f.agentId;
              return (
                <button key={f.agentId} onClick={() => chooseFlavor(r.role, f.agentId)} title={f.hint}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 ${TYPE.control} transition ${on
                    ? 'border-secondary-900 dark:border-white bg-secondary-100 dark:bg-secondary-800 font-semibold text-secondary-900 dark:text-white'
                    : 'border-secondary-200 dark:border-secondary-600 text-secondary-600 dark:text-secondary-300 hover:border-secondary-400'}`}>
                  {on && <Check className="h-3 w-3" />}{f.name} <span className="opacity-60">· {f.flavor}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {error && <p className={`${TYPE.control} text-secondary-500`}>{error}</p>}
      <button onClick={add} disabled={resolving || !crewCount}
        className={`inline-flex items-center gap-1.5 rounded-xl bg-secondary-900 dark:bg-white px-4 py-2.5 ${TYPE.sectionTitle} text-white dark:text-secondary-900 disabled:opacity-40`}>
        {resolving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Clapperboard className="h-4 w-4" />}
        Add crew{crewCount ? ` (${crewCount})` : ''}
      </button>
    </div>
  );
};

export default MediaCrewPicker;
